import axios from "axios";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import deletegif from "../../../assets/gif/bin.gif";
import dollor from "../../../assets/icons/dollar.svg";
import percentage from "../../../assets/icons/percentage.svg";
import DeleteConfirmationModal from "../../../components/ConfirmationModals/DeleteConfirmationModal";
import InputWithIcon from "../../../components/InputWithIcon";
import Modal from "../../../components/Modal/Modal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const initialData = {
  initial_list_date: "",
  initial_expiration_date: "",
  brokerage_commission: "",
  asking_price: "",
  asking_cap_rate: "",
  portfolio: 0,
  properties: [], // For portfolio properties
};

const ChangeProposalStatusModal = ({
  showModal,
  onClose,
  onCallApi,
  id,
  leadId,
  pipelineDetails,
}) => {
  const [config] = useAuth();
  const [disable, setDisable] = useState(false);
  const [error, setError] = useState();
  const [formData, setFormData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [disableDelete, setDisableDelete] = useState(false);

  useEffect(() => {
    if (id && showModal) {
      setLoading(true);
      axios
        .get(`${BASE_URL}/getby-Id/${id}`, config)
        .then((res) => {
          const data = res?.data?.LeadsList;
          if (data?.portfolio === 1) {
            setFormData({
              portfolio: 1,
              properties:
                data.link?.portfolois?.map((p) => ({
                  property_id: p.property?.id,
                  property_name: p.property?.property_name || "Unnamed Property",
                  street_address: p.property?.street_address || "",
                  city: p.property?.city || "",
                  state: p.property?.state || "",
                  asking_price: p.property?.asking_price || "0",
                  asking_cap_rate: p.property?.asking_cap_rate || "0",
                })) || [],
            });
          } else {
            setFormData({
              portfolio: 0,
              asking_price: data.link?.asking_price_format || "0",
              asking_cap_rate: data.link?.asking_cap_rate_format || "0",
              street_address: data.link?.street_address || "",
              city: data.link?.city || "",
              state: data.link?.state || "",
            });
          }
        })
        .catch((err) => {
          if (
            err.response?.data?.message &&
            err.response?.data?.message !== "Too Many Attempts."
          ) {
            NotificationManager.error(err.response?.data?.message);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [id, showModal]);


  const handleChange = (value, name) => {
    setError({ ...error, [name]: "" });
    setFormData({ ...formData, [name]: value });
  };

  const handleOpenDeleteModal = (propertyId) => {
    setSelectedId(propertyId);
    setShowDeleteModal(true);
  };

  const handlePropertyChange = (index, field, value) => {
    const updatedProperties = [...formData.properties];
    updatedProperties[index][field] = value;
    setFormData({ ...formData, properties: updatedProperties });
  };

  const handleClose = () => {
    setFormData(initialData);
    onClose();
    setError();
  };

  const handleDelete = () => {
  setDisableDelete(true)
  axios
    .delete(`${BASE_URL}/disabled-portfolio-property/${selectedId}`, config)
    .then((res) => {
      setSelectedId("")
      onCallApi()
      setShowDeleteModal(false)
      if (res?.data?.message) {
        NotificationManager.success(res?.data?.message)
      }
    })
    .catch((err) => {
      if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
        NotificationManager.error(err.response?.data?.message)
      }
    })
    .finally(() => setDisableDelete(false))
}

  const handleUpdateStatus = (e) => {
    e.preventDefault();
    setDisable(true);

    const dataToSend = {
      lead_id: leadId,
      initial_list_date: formData?.initial_list_date,
      initial_expiration_date: formData?.initial_expiration_date,
      brokerage_commission: formData?.brokerage_commission,
      ...(formData.portfolio === 1
        ? {
          properties: formData.properties.map((p) => ({
            property_id: p.property_id,
            asking_price: p.asking_price,
            asking_cap_rate: p.asking_cap_rate,
          })),
        }
        : {
          asking_price: formData.asking_price,
          asking_cap_rate: formData.asking_cap_rate,
        }),
    };

    axios
      .post(`${BASE_URL}/markAs-Proposal/${id}`, dataToSend, config)
      .then((res) => {
        onCallApi();
        onClose();
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        setDisable(false);
        setError(err.response.data.errors);
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  return (
    <Modal
      title={"Convert To Exclusives"}
      desc={`Add the exclusive property information.`}
      show={showModal}
      onClose={onClose}
    >
      <form onSubmit={handleUpdateStatus}>
        <div className="mt-6">
          <label className="dark-H head-4 mb-2 required:*:">
            Initial List Date<span className="red-D">*</span>
          </label>
          <DatePicker
            className="body-N"
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            maxDate={new Date()}
            selected={formData.initial_list_date}
            onChange={(date) => handleChange(date, "initial_list_date")}
          />
          {error?.initial_list_date && (
            <span className="body-S red-D">{error?.initial_list_date}</span>
          )}
        </div>

        <div className="mt-6">
          <label className="dark-H head-4 mb-2 required:*:">
            Listing Expiration Date<span className="red-D">*</span>
          </label>
          <DatePicker
            className="body-N"
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            selected={formData.initial_expiration_date}
            minDate={new Date()}
            onChange={(date) => handleChange(date, "initial_expiration_date")}
          />
          {error?.initial_expiration_date && (
            <span className="body-S red-D">{error?.initial_expiration_date}</span>
          )}
        </div>

        <div className="mt-6">
          <label className="dark-H head-4 mb-2 required:*:">
            Brokerage Commission<span className="red-D">*</span>
          </label>
          <InputWithIcon
            icon={percentage}
            className="body-N"
            name="brokerage_commission"
            type="number"
            placeholder="Write Here"
            value={formData?.brokerage_commission}
            onChange={(e) => handleChange(e.target.value, "brokerage_commission")}
            onWheel={(e) => e.target.blur()}
          />
          {error?.brokerage_commission && (
            <span className="body-S red-D">{error?.brokerage_commission}</span>
          )}
        </div>

        {formData.portfolio === 1 ? (
          formData.properties?.map((p, index) => (
            <div key={p.property_id} className="mt-6 border-b pb-4 relative">

              {/* Delete Icon */}
              <button
                type="button"
                onClick={() => handleOpenDeleteModal(p.property_id)}
                className="absolute top-0 right-0"
              >
                <img src={deletegif} alt="binimg" height={30} width={30} />
              </button>

              <label className="dark-H head-4 mb-2 block">Property Name & Address</label>

              <div className="body-N">
                {p.property_name} -{" "}
                <span className="body-N">
                  {p.street_address}
                  {p.city && `, ${p.city}`}
                  {p.state && `, ${p.state}`}
                  {p.zip && ` ${p.zip}`}
                </span>
              </div>

              <div className="flex gap-6 mt-2">
                <div className="w-1/2">
                  <label className="dark-H head-4 mb-2 block">Asking Price</label>
                  <InputWithIcon
                    icon={dollor}
                    className="body-N w-full"
                    type="number"
                    placeholder="Asking Price"
                    value={p.asking_price}
                    onChange={(e) =>
                      handlePropertyChange(index, "asking_price", e.target.value)
                    }
                  />
                </div>

                <div className="w-1/2">
                  <label className="dark-H head-4 mb-2 block">Asking Cap Rate</label>
                  <InputWithIcon
                    icon={percentage}
                    className="body-N w-full"
                    type="number"
                    placeholder="Asking Cap Rate"
                    value={p.asking_cap_rate}
                    onChange={(e) =>
                      handlePropertyChange(index, "asking_cap_rate", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))
        ) : (
          <>

            <div className="mt-6">
              <label className="dark-H head-4 mb-2 required:*:">Asking Price</label>
              <InputWithIcon
                icon={dollor}
                className="body-N"
                type="number"
                placeholder="Write Here"
                value={formData?.asking_price}
                onChange={(e) => handleChange(e.target.value, "asking_price")}
              />
            </div>

            <div className="mt-6">
              <label className="dark-H head-4 mb-2 required:*:">Asking Cap Rate</label>
              <InputWithIcon
                icon={percentage}
                className="body-N"
                type="number"
                placeholder="Write Here"
                value={formData?.asking_cap_rate}
                onChange={(e) => handleChange(e.target.value, "asking_cap_rate")}
              />
            </div>
          </>
        )}

        <div className="mt-7">
          <button
            type="submit"
            className="save-button light-L head-5 green-bg-H"
          >
            Save
          </button>
          <button type="button" onClick={handleClose} className="green-H ml-5">
            Cancel
          </button>
        </div>
      </form>

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />
    </Modal>
  );
};

export default ChangeProposalStatusModal;
